import { getSessionIdleMinutes } from '../config/runtimeConfig';

/**
 * One place for the browser side of a session: the keys it lives under, and the inactivity clock
 * that decides when it is over. Every sign-out path has to clear exactly the same keys, and every
 * guard has to ask the same question about idleness, or a session ends in one tab and survives in
 * another.
 */
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';

// Deliberately in localStorage rather than sessionStorage: it is shared by every tab of the same
// browser, so working in one tab keeps the others alive and timing out ends all of them together.
const LAST_ACTIVITY_KEY = 'lastActivityAt';

const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;

export const getIdleTimeoutMs = () => {
  const configured = Number(getSessionIdleMinutes());
  const minutes = Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_IDLE_TIMEOUT_MINUTES;
  return minutes * 60 * 1000;
};

export const getStoredToken = () => sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);

export const getStoredRefreshToken = () =>
  sessionStorage.getItem(REFRESH_TOKEN_KEY) || localStorage.getItem(REFRESH_TOKEN_KEY);

export const getStoredUser = () => sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);

export const saveStoredTokens = (token: string, refreshToken: string) => {
  [sessionStorage, localStorage].forEach((store) => {
    store.setItem(TOKEN_KEY, token);
    store.setItem(REFRESH_TOKEN_KEY, refreshToken);
  });
};

export const saveStoredUser = (user: unknown) => {
  const serialized = JSON.stringify(user);
  [sessionStorage, localStorage].forEach((store) => store.setItem(USER_KEY, serialized));
};

export const markSessionActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

export const clearStoredSession = () => {
  [sessionStorage, localStorage].forEach((store) => {
    store.removeItem(TOKEN_KEY);
    store.removeItem(REFRESH_TOKEN_KEY);
    store.removeItem(USER_KEY);
  });
  localStorage.removeItem(LAST_ACTIVITY_KEY);
};

/**
 * A stored token with no activity stamp is a session that predates this policy, or one whose stamp
 * was cleared behind our back. Neither can be dated, so neither is trusted: the user signs in again.
 */
export const isSessionIdleExpired = () => {
  const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  if (!Number.isFinite(lastActivity) || lastActivity <= 0) return true;

  return Date.now() - lastActivity > getIdleTimeoutMs();
};

/**
 * A session found already idle-expired when the app boots has its token cleared before anything can
 * reach the network, so the refresh token is parked here for the session watcher to revoke once it
 * mounts. Covers the browser that was closed and reopened a day later.
 */
let staleRefreshToken: string | null = null;

export const rememberStaleRefreshToken = (token: string | null) => {
  staleRefreshToken = token;
};

export const takeStaleRefreshToken = () => {
  const token = staleRefreshToken;
  staleRefreshToken = null;
  return token;
};

export const SESSION_TIMEOUT_REDIRECT = '/login?reason=timeout';
